import { ReactNode, useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'cmdk';
import { ListFilter } from 'lucide-react';

import { EQLFilterContent } from './EQLFilterContent';
import { EQLFilterFieldConfig } from './types';
import { useEQLFilter } from './useEQLFilter';

import css from './styles.module.css';

export interface EQLFilterTriggerProps {
  config: EQLFilterFieldConfig[];
  onSelect: (field: EQLFilterFieldConfig) => void;
  /** Overrides the default "Add Filter" button. */
  children?: ReactNode;
  placeholder?: string;
}

export function EQLFilterTrigger({
  config,
  onSelect,
  children,
  placeholder = 'Filter...',
}: EQLFilterTriggerProps) {
  const { open, close } = useEQLFilter();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const selectField = (field: EQLFilterFieldConfig) => {
    onSelect(field);
    close();
  };

  return (
    <>
      <Popover.Trigger asChild>
        {children ?? (
          <button type="button" className={css.trigger}>
            <ListFilter />
            Add filter
          </button>
        )}
      </Popover.Trigger>

      <EQLFilterContent align="start" side="bottom">
        <div className={css.pickerColumn}>
          <Command className={css.command} loop>
            <div className={css.searchRow}>
              <CommandInput
                autoFocus
                value={search}
                onValueChange={setSearch}
                placeholder={placeholder}
              />
            </div>

            <CommandList className={css.list}>
              <CommandEmpty className={css.empty}>No fields found</CommandEmpty>
              <CommandGroup>
                {config.map((field) => (
                  <CommandItem
                    key={field.name}
                    value={field.name}
                    onSelect={() => selectField(field)}
                    className={css.item}
                  >
                    <span className={css.icon}>{field.icon}</span>
                    <span className={css.label}>{field.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      </EQLFilterContent>
    </>
  );
}
